import { useEffect, useRef } from "react";

/**
 * Gold "wipe" transition for fullscreen odds/bong. Plays an alpha (VP8) WebM on
 * top of everything. The green screen was chroma-keyed out (transparent), so
 * only the sweeping gold ribbons are opaque. At `coverTime` the ribbons fully
 * cover the screen — that is the exact moment onCover() fires so the underlying
 * content can be swapped invisibly. onEnd() fires when the wipe finishes.
 *
 * Muted + autoPlay so it always starts (reliable, frame-accurate timing). No
 * CSS opacity fades — the layer mounts/unmounts; transparency is baked into the
 * video alpha, so it stays chromakey-safe.
 */
export function WipeTransition({ show, token, src, coverTime = 1.1, onCover, onEnd }) {
  const videoRef = useRef(null);
  const rafRef = useRef(null);
  const coverFiredRef = useRef(false);
  const endFiredRef = useRef(false);
  const coverTimerRef = useRef(null);
  const endTimerRef = useRef(null);

  useEffect(() => {
    if (!show) return undefined;
    coverFiredRef.current = false;
    endFiredRef.current = false;

    const fireCover = () => {
      if (coverFiredRef.current) return;
      coverFiredRef.current = true;
      if (onCover) onCover();
    };
    const fireEnd = () => {
      if (endFiredRef.current) return;
      endFiredRef.current = true;
      fireCover(); // guarantee content swapped even on very short/failed plays
      if (onEnd) onEnd();
    };

    const v = videoRef.current;
    if (v) {
      try {
        v.currentTime = 0;
        v.muted = true;
        const p = v.play();
        if (p && p.catch) p.catch(() => {});
      } catch (e) { /* ignore */ }
    }

    // Frame-accurate watch of playback position
    const tick = () => {
      const vid = videoRef.current;
      if (vid && vid.currentTime >= coverTime) fireCover();
      if (!coverFiredRef.current || !endFiredRef.current) {
        rafRef.current = requestAnimationFrame(tick);
      }
    };
    rafRef.current = requestAnimationFrame(tick);

    // Safety fallbacks in case timeupdate/ended never fire
    coverTimerRef.current = setTimeout(fireCover, Math.round(coverTime * 1000));
    endTimerRef.current = setTimeout(fireEnd, 3000);

    const onEnded = () => fireEnd();
    if (v) v.addEventListener("ended", onEnded);

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (coverTimerRef.current) clearTimeout(coverTimerRef.current);
      if (endTimerRef.current) clearTimeout(endTimerRef.current);
      if (v) v.removeEventListener("ended", onEnded);
    };
    // Re-run whenever a new transition is triggered
  }, [show, token]);

  if (!show) return null;

  return (
    <div
      data-testid="wipe-transition"
      style={{
        position: "absolute",
        inset: 0,
        width: "100vw",
        height: "100vh",
        zIndex: 90,
        pointerEvents: "none",
      }}
    >
      <video
        key={token}
        ref={videoRef}
        src={src}
        autoPlay
        muted
        playsInline
        controls={false}
        style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
      />
    </div>
  );
}
